/**
 * Rate API (pms-b.rrcbase.com) — talks to the external rate service directly from
 * the browser, without going through this app's backend.
 *
 * That is deliberate and it is the exception, not the pattern: every other
 * resource in this app goes through `/api/...`. Rates are reached this way so the
 * passkey stays something a person types rather than something the server holds
 * on their behalf — the salary gate collects it, and it is kept only for a few
 * hours (see PASSKEY_TTL_MS).
 *
 * The backend keeps a passkey of its own regardless, because payroll computation
 * runs on a schedule with no browser to ask. This client covers only the screens
 * where a person is present.
 *
 * Shape of the data:
 *   * A rate is a PERIOD — `effective_from` .. `effective_to`, both YYYY-MM. One
 *     period per rate CHANGE, not per month. Periods may not overlap; the API
 *     rejects an overlap with 409 and names the period it hit.
 *   * `effective_to` null means open-ended, which blocks every later period until
 *     it is closed.
 *   * A month with no period of its own inherits the most recent earlier one, so
 *     the API's `?month=` lookup is never used here — it 404s on such a month.
 *   * `rate_id` identifies a period, and is the only unambiguous selector for an
 *     edit that also moves the month range.
 */

import { env } from '@/config/env';

const PASSKEY_STORAGE_KEY = 'salary-rate-passkey';

/**
 * How long a typed pass key stays valid. Long enough that nobody retypes it
 * through a working day's worth of payroll edits, short enough that an unattended
 * machine does not stay unlocked overnight.
 */
export const PASSKEY_TTL_MS = 4 * 60 * 60 * 1000;

interface StoredPasskey {
  key: string;
  /** Epoch ms. Past this, the key is discarded on the next read. */
  expiresAt: number;
}

/** One rate period, exactly as the Rate API stores it. */
export interface RatePeriod {
  rate_id: number;
  /** The profile id — this system's `users.id`. */
  id: string;
  name: string | null;
  rate: number;
  /** YYYY-MM */
  effective_from: string;
  /** YYYY-MM, or null when open-ended. */
  effective_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface RatePeriodPayload {
  rate: number;
  effective_from: string;
  effective_to?: string | null;
}

/** Thrown for every non-OK response, so callers can branch on `status`. */
export class RateApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RateApiError';
    this.status = status;
  }
}

interface RateApiEnvelope {
  ok?: boolean;
  data?: unknown;
  error?: string;
}

// ── passkey ────────────────────────────────────────────────────────────────────

/**
 * localStorage rather than sessionStorage, so a reload, a second tab or a closed
 * laptop lid does not force the key to be retyped. The expiry is what limits it
 * instead, and an expired entry is dropped the first time it is read.
 *
 * Every access is guarded: a browser in private mode can throw on storage
 * entirely, and a hand-edited entry can be unparseable.
 */
function readStored(): StoredPasskey | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(PASSKEY_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: StoredPasskey;
  try {
    parsed = JSON.parse(raw) as StoredPasskey;
  } catch {
    clearPasskey();
    return null;
  }

  if (!parsed?.key || typeof parsed.expiresAt !== 'number') {
    clearPasskey();
    return null;
  }
  if (Date.now() >= parsed.expiresAt) {
    clearPasskey();
    return null;
  }
  return parsed;
}

function getPasskey(): string {
  return readStored()?.key ?? '';
}

/**
 * The admin key, for the request interceptor that attaches it to payroll calls.
 * Empty when no admin has unlocked on this browser.
 */
export function getAdminPasskey(): string {
  return getPasskey();
}

export function setPasskey(passkey: string): void {
  const entry: StoredPasskey = {
    key: passkey.trim(),
    expiresAt: Date.now() + PASSKEY_TTL_MS,
  };
  try {
    localStorage.setItem(PASSKEY_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // The provider keeps its own in-memory copy for this page load.
  }
}

export function clearPasskey(): void {
  try {
    localStorage.removeItem(PASSKEY_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export const hasPasskey = (): boolean => getPasskey().length > 0;

/** Epoch ms at which the stored key expires, or null when nothing is stored. */
export function passkeyExpiresAt(): number | null {
  return readStored()?.expiresAt ?? null;
}

// ── transport ──────────────────────────────────────────────────────────────────

/**
 * The API answers `{ok:true,data}` on success and `{ok:false,error}` on failure,
 * so the body has to be read before a response can be called good — an HTTP 200
 * carrying `ok:false` is still a failure.
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
  passkeyOverride?: string,
): Promise<T> {
  const passkey = passkeyOverride ?? getPasskey();
  if (!passkey) throw new RateApiError('Salary pass key not entered', 401);

  let res: Response;
  try {
    res = await fetch(`${env.RATE_API_URL}${path}`, {
      ...init,
      headers: {
        'x-passkey': passkey,
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    // fetch() rejects without a status for a network failure and a CORS rejection
    // alike, and the browser only reports the latter to the console — so name
    // both rather than guessing at one.
    throw new RateApiError('Rate API unreachable — network or CORS', 0);
  }

  let body: RateApiEnvelope | null = null;
  try {
    body = (await res.json()) as RateApiEnvelope;
  } catch {
    // The status still carries the outcome.
  }

  if (!res.ok || body?.ok === false) {
    throw new RateApiError(body?.error ?? `Rate API error (HTTP ${res.status})`, res.status);
  }

  return body?.data as T;
}

// ── rates ──────────────────────────────────────────────────────────────────────

export const rateApiService = {
  /** Every period of one employee, oldest first. Empty when they have no rate. */
  async getHistory(userId: string): Promise<RatePeriod[]> {
    try {
      const data = await request<RatePeriod[]>(`/api/rates/${encodeURIComponent(userId)}/history`);
      return [...(data ?? [])].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
    } catch (e) {
      // No rate on record is an ordinary answer — a new joiner has none yet.
      if (e instanceof RateApiError && e.status === 404) return [];
      throw e;
    }
  },

  async createPeriod(userId: string, input: RatePeriodPayload, employeeName?: string): Promise<RatePeriod> {
    return request<RatePeriod>('/api/rates', {
      method: 'POST',
      body: JSON.stringify({
        id: userId,
        rate: input.rate,
        effective_from: input.effective_from,
        effective_to: input.effective_to ?? null,
        ...(employeeName ? { name: employeeName } : {}),
      }),
    });
  },

  async updatePeriod(userId: string, rateId: number, input: Partial<RatePeriodPayload>): Promise<RatePeriod> {
    return request<RatePeriod>('/api/rates', {
      method: 'PUT',
      body: JSON.stringify({ id: userId, rate_id: rateId, ...input }),
    });
  },

  async deletePeriod(userId: string, rateId: number): Promise<void> {
    await request<unknown>(
      `/api/rates/${encodeURIComponent(userId)}?rate_id=${rateId}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Check a passkey with the cheapest authenticated call there is. `/api/me`
   * would suit it better, but this client is scoped to the rate routes, so the
   * list read stands in as the probe.
   */
  async verifyPasskey(passkey: string): Promise<boolean> {
    try {
      await request<RatePeriod[]>('/api/rates', {}, passkey);
      return true;
    } catch (e) {
      if (e instanceof RateApiError && (e.status === 401 || e.status === 403)) return false;
      throw e;
    }
  },
};
